import { useEffect, useMemo, useState } from "react";
import { DEFAULT_HIGHCHARTS_PIE_CHART_OPTIONS } from "@constants/highcharts";
import { HighchartsChartContainer } from "../../../../common/highcharts-chart-container/highcharts-chart-container";
import merge from "lodash/merge";
import { WorkflowReportCompletionRateDataType } from "@idw-types/data/reports";
import { resources } from "@constants/hardcoded-strings";
import { COMPLETE_COLOR, INCOMPLETE_COLOR } from "@constants/colors";
import { WorkflowReportSearchFiltersState } from "@idw-types/state/workflow-report-state"; 
import format from "date-fns/format";
import startOfDay from "date-fns/startOfDay";
import endOfDay from "date-fns/endOfDay";
import { SERVER_DATE_TIME_FORMAT } from "@constants/date-formats";
import { PolicyDecisionTypes } from "@enums/policy-decision-types";
import { TimeRangeTypes } from "@enums/time-range-types";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import { navigateToUtilWithQueryParameters } from "@utils/navigation-util";
import { REPORTS_TRANSACTIONS_URL } from "@constants/routes";

type WorkflowReportCompletionRatesPieChartProps = {
    completionRatesResponse: WorkflowReportCompletionRateDataType;
    searchFilters: WorkflowReportSearchFiltersState;
};


export const WorkflowReportCompletionRatesPieChart = (props: WorkflowReportCompletionRatesPieChartProps) => {
    const { completionRatesResponse, searchFilters } = props;
    const navigate = useNavigate();
    const routerLocation = useLocation();

    const startDate = useMemo(() => {
        return format(startOfDay(searchFilters.startDate), SERVER_DATE_TIME_FORMAT);
    }, [searchFilters.startDate]);

    const endDate = useMemo(() => {
        return format(endOfDay(searchFilters.endDate), SERVER_DATE_TIME_FORMAT);
    }, [searchFilters.endDate]);

    const data = useMemo(() => {
        const dataToDisplay: Array<{ name: string; color: string; y: number; }> = [
            {
                name: resources.charts.workflowReportCompletionRates.labels.complete,
                color: COMPLETE_COLOR,
                y: completionRatesResponse.complete
            },
            {
                name: resources.charts.workflowReportCompletionRates.labels.incomplete,
                color: INCOMPLETE_COLOR,
                y: completionRatesResponse.incomplete
            }
        ];

        return dataToDisplay;
    }, [completionRatesResponse]);

    const [chartOptions, setChartOptions] = useState<Highcharts.Options>(merge({}, DEFAULT_HIGHCHARTS_PIE_CHART_OPTIONS, {
        chart: {
            height: "320"
        },
        title: {
            text: resources.charts.workflowReportCompletionRates.title,
        },
        series: [{
            data: data
        }],
        plotOptions: {
            pie: {
                cursor: "pointer",
                events: {
                    click: (event: any) => {
                        const name = event.point?.name;

                        const queryParametersObject: any = {
                            startDate: startDate,
                            endDate: endDate,
                            timeRangeType: TimeRangeTypes.CUSTOM,
                            workflowIds: searchFilters.workflowId,
                            shouldSearchOnLoad: "true",
                            includeArchivedServicesAndWorkflows: `${searchFilters.includeArchivedWorkflows}`
                        };

                        if (searchFilters.appId) {
                            queryParametersObject.appId = searchFilters.appId;
                        }

                        if (searchFilters.country) {
                            queryParametersObject.country = searchFilters.country;
                        }

                        if (name === resources.charts.workflowReportCompletionRates.labels.complete) {
                            queryParametersObject.policyDecisions = [PolicyDecisionTypes.DENIED, PolicyDecisionTypes.APPROVED].join(",");
                        } else {
                            queryParametersObject.policyDecisions = [PolicyDecisionTypes.OBLIGATION, PolicyDecisionTypes.NO_DECISION].join(",");
                        }

                        const queryParameters = createSearchParams(queryParametersObject).toString();
                        navigateToUtilWithQueryParameters(REPORTS_TRANSACTIONS_URL, navigate, routerLocation.pathname, queryParameters);
                    }
                }
            }
        }
    }));

    useEffect(() => {
        const newChartOptions: Highcharts.Options = merge({}, chartOptions, {
            series: [{
                data: data
            }]
        });

        setChartOptions(newChartOptions);

    }, [data]);

    return <HighchartsChartContainer options={chartOptions} />;
};